import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, CheckCircle, Coffee, Star, Award, Crown, Gift, Banknote, Construction } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { usePiAuth } from "../contexts/PiAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Donate = () => {
  const [searchParams] = useSearchParams();
  const { createPiPayment } = usePiAuth();
  const [donationLevels, setDonationLevels] = useState([]);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("etransfer");
  const etransferEmail = "gold.manson@gmail.com";

  useEffect(() => {
    fetchDonationLevels();
    checkStatus();
  }, []);

  const fetchDonationLevels = async () => {
    try {
      const response = await axios.get(`${API}/donations/levels`);
      setDonationLevels(response.data.levels);
      // Default to middle tier
      if (response.data.levels.length > 0) {
        setSelectedAmount(response.data.levels[1].amount);
      }
    } catch (e) {
      console.error("Error fetching donation levels:", e);
      // Fallback levels
      setDonationLevels([
        { amount: 10, label: "Coffee", description: "Buy us a coffee" },
        { amount: 25, label: "Supporter", description: "Support local journalism" },
        { amount: 50, label: "Champion", description: "Champion the valley" },
        { amount: 100, label: "Patron", description: "Become a patron" },
        { amount: 250, label: "Benefactor", description: "Major supporter" }
      ]);
      setSelectedAmount(25);
    }
  };

  const checkStatus = () => {
    const status = searchParams.get("status");
    if (status === "success") {
      setSuccess(true);
      toast.success("Thank you for your donation!");
    } else if (status === "cancelled") {
      toast.error("Donation was cancelled");
    }
  };

  const getLevelIcon = (label) => {
    const icons = {
      "Coffee": Coffee,
      "Supporter": Heart,
      "Champion": Star,
      "Patron": Award,
      "Benefactor": Crown
    };
    return icons[label] || Gift;
  };

  const getFinalAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const handleDonate = async () => {
    const amount = getFinalAmount();
    if (!amount || amount < 1) {
      toast.error("Please select or enter a donation amount");
      return;
    }

    setProcessing(true);

    if (paymentMethod === "pi") {
      try {
        await createPiPayment({
          amount,
          memo: `EK Valleys Donation — $${amount}`,
          metadata: {
            product_type: "donation",
            donor_name: donorName || "Anonymous",
            donor_email: donorEmail || "",
            message: message || "",
          },
        });
        setSuccess(true);
        toast.success("Thank you for your Pi donation!");
      } catch (err) {
        toast.error(err?.message || "Pi payment failed or was cancelled.");
      }
      setProcessing(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (e) {
      console.error("Error creating donation:", e);
      toast.error("Failed to process donation. Please try again.");
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#EFEDE5]">
        <NavbarLight />
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 bg-[#2C4A3B] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#D4A017]" />
            </div>
            <h1 className="font-heading text-4xl font-bold text-[#1A1915] mb-4">
              Thank You!
            </h1>
            <p className="text-[#5C5A52] text-lg mb-8">
              Your generosity helps keep ekvalleys.com running and supports local content for the East Kootenay community.
            </p>
            <Button 
              onClick={() => window.location.href = "/"}
              className="bg-[#2C4A3B] text-white"
            >
              Return Home
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <NavbarLight />

      {/* Header */}
      <section className="pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <Heart className="w-12 h-12 text-[#D4A017] mx-auto mb-4" />
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915] mb-4">
            Support ekvalleys.com
          </h1>
          <p className="text-[#5C5A52] text-lg">
            Help us keep the East Kootenay's digital home running. Your donation supports local content, business listings, and community resources.
          </p>
        </div>
      </section>

      {/* Development Notice */}
      <section className="px-6 md:px-12 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#D4A017]/10 border-2 border-[#D4A017]/40 rounded-xl p-5 flex items-start gap-3" data-testid="donate-dev-notice">
            <Construction className="w-6 h-6 text-[#D4A017] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1A1915]">Early Development & Launch Phase</h3>
              <p className="text-[#5C5A52] text-sm">
                EkValleys is currently in the early development and launch phase. While final registration and banking are being completed, initial launch partnerships are being coordinated directly via <strong>E-Transfer</strong>. Send to <strong>{etransferEmail}</strong> with "Donation" in the message.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Donation Levels */}
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8 mb-6">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-6">
              Choose Your Level
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {donationLevels.map((level) => {
                const Icon = getLevelIcon(level.label);
                const isSelected = selectedAmount === level.amount && !customAmount;
                return (
                  <button
                    key={level.amount}
                    onClick={() => {
                      setSelectedAmount(level.amount);
                      setCustomAmount("");
                    }}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      isSelected 
                        ? 'border-[#D4A017] bg-[#D4A017]/10' 
                        : 'border-[#D1CFC5] hover:border-[#2C4A3B]'
                    }`}
                    data-testid={`donation-level-${level.amount}`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-[#D4A017]' : 'text-[#5C5A52]'}`} />
                    <p className="font-bold text-[#1A1915]">${level.amount}</p>
                    <p className="text-xs text-[#5C5A52]">{level.label}</p>
                  </button>
                );
              })}
            </div>

            {/* Custom Amount */}
            <div className="border-t-2 border-[#D1CFC5] pt-6">
              <Label className="text-[#1A1915] font-medium">Or enter a custom amount</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[#5C5A52] text-lg">$</span>
                <Input
                  type="number"
                  placeholder="Custom amount"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  className="bg-white border-2 border-[#D1CFC5] max-w-[150px]"
                  min="1"
                  data-testid="custom-amount-input"
                />
                <span className="text-[#5C5A52]">CAD</span>
              </div>
            </div>
          </div>

          {/* Donor Info (Optional) */}
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8 mb-6">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-2">
              Your Information
            </h2>
            <p className="text-[#5C5A52] text-sm mb-6">Optional — leave blank to donate anonymously</p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="donorName" className="text-[#1A1915] font-medium">Name</Label>
                <Input
                  id="donorName"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  className="mt-2 bg-white border-2 border-[#D1CFC5]"
                  placeholder="Your name (optional)"
                  data-testid="donor-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="donorEmail" className="text-[#1A1915] font-medium">Email</Label>
                <Input
                  id="donorEmail"
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="mt-2 bg-white border-2 border-[#D1CFC5]"
                  placeholder="your@email.com (optional)"
                  data-testid="donor-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="message" className="text-[#1A1915] font-medium">Message</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-2 bg-white border-2 border-[#D1CFC5]"
                  placeholder="Leave a message (optional)"
                  rows={3}
                  data-testid="donation-message-input"
                />
              </div>
            </div>
          </div>

          {/* Payment Method & Submit */}
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8 mb-6">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-6">
              Payment Method
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button type="button" onClick={() => setPaymentMethod("etransfer")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${paymentMethod === "etransfer" ? "border-[#2C4A3B] bg-[#2C4A3B]/5" : "border-[#D1CFC5]"}`}
                data-testid="donate-pay-etransfer">
                <Banknote className="w-5 h-5 text-[#2C4A3B]" />
                <div className="text-left">
                  <p className="font-semibold text-[#1A1915] text-sm">E-Transfer</p>
                  <p className="text-xs text-[#5C5A52]">Send to {etransferEmail}</p>
                </div>
              </button>
              <button type="button" onClick={() => setPaymentMethod("pi")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${paymentMethod === "pi" ? "border-[#7B4FC4] bg-[#7B4FC4]/5" : "border-[#D1CFC5]"}`}
                data-testid="donate-pay-pi">
                <Construction className="w-5 h-5 text-[#7B4FC4]" />
                <div className="text-left">
                  <p className="font-semibold text-[#1A1915] text-sm">Pay with Pi</p>
                  <p className="text-xs text-[#5C5A52]">Via Pi Wallet</p>
                </div>
              </button>
              <button type="button" disabled
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-[#D1CFC5] opacity-50 cursor-not-allowed"
                data-testid="donate-pay-stripe">
                <Heart className="w-5 h-5 text-[#5C5A52]" />
                <div className="text-left">
                  <p className="font-semibold text-[#5C5A52] text-sm">Pay with Card</p>
                  <p className="text-xs text-[#5C5A52]">Coming Soon</p>
                </div>
              </button>
            </div>

            {paymentMethod === "pi" && (
              <div className="bg-[#7B4FC4]/5 border-2 border-[#7B4FC4]/30 rounded-xl p-5 mb-4" data-testid="pi-donation-instructions">
                <h3 className="font-heading text-lg font-semibold text-[#1A1915] mb-2">Pay with Pi</h3>
                <p className="text-sm text-[#5C5A52]">Click &quot;Donate&quot; below to open Pi Wallet and complete your donation.</p>
              </div>
            )}

            {paymentMethod === "etransfer" && (
              <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid="etransfer-instructions">
                <h3 className="font-heading text-lg font-semibold text-[#1A1915] mb-3">E-Transfer Instructions</h3>
                <ol className="space-y-2 text-sm text-[#5C5A52]">
                  <li className="flex gap-2"><span className="font-bold text-[#1A1915]">1.</span> Open your banking app and select Interac E-Transfer</li>
                  <li className="flex gap-2"><span className="font-bold text-[#1A1915]">2.</span> Send <strong className="text-[#1A1915]">${getFinalAmount() || 0} CAD</strong> to <strong className="text-[#1A1915]">{etransferEmail}</strong></li>
                  <li className="flex gap-2"><span className="font-bold text-[#1A1915]">3.</span> In the message field, include "Donation" and your name (optional)</li>
                </ol>
              </div>
            )}
          </div>

          <div className="text-center">
            <p className="text-[#5C5A52] text-sm">
              Thank you for supporting the East Kootenay community.
            </p>
          </div>
        </div>
      </section>

      {/* Why Support */}
      <section className="py-12 px-6 md:px-12 bg-[#F7F6F2] border-t-2 border-[#D1CFC5]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-4">
            Why Support Us?
          </h2>
          <p className="text-[#5C5A52]">
            ekvalleys.com is built for the East Kootenay community — a place where every business, service, and experience is easy to find. Your donations help cover hosting costs, maintain the directory, and create local content. Thank you for being part of this.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Donate;
